import * as React from "react";
import { styled } from "@mui/material/styles";
import { useEffect } from "react";
import { KeyValue, Query } from "../../interfaces";
import { Divider, Fade, Grid, Link, Typography } from "@mui/material";
import { CardListCondensedItem } from "../SearchResultCard/SearchResultCard";
import LeavingFDICDialog from "../LeavingFDICDialog";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import {
  formatDate,
  bankClassLookup,
  getPrimaryRegulator,
  isDefinedAndTrue,
} from "../../common/services/Utilities";
import LinkToolTip from "../../common/components/LinkToolTip";

const PREFIX = "SodInstitutionDetails";

const classes = {
  subHeader: `${PREFIX}-subHeader`,
  gridItem: `${PREFIX}-gridItem`,
  succeedingInstInfo: `${PREFIX}-succeedingInstInfo`,
  tooltipPopper: `${PREFIX}-tooltipPopper`,
  condensedTooltip: `${PREFIX}-condensedTooltip`,
  text: `${PREFIX}-text`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.subHeader}`]: {
    "display": "flex",
    "position": "relative",
    "& > p ": {
      color: theme.palette.primary.main,
      position: "absolute",
      right: 0,
    },
  },

  [`& .${classes.gridItem}`]: {
    "& > div": {
      paddingBottom: "1rem",
    },
  },

  [`& .${classes.succeedingInstInfo}`]: {
    lineHeight: "1rem",
    marginBottom: "1rem",
  },

  [`& .${classes.tooltipPopper}`]: {
    maxWidth: "100%",
  },

  [`& .${classes.condensedTooltip}`]: {
    maxWidth: 300,
  },

  [`& .${classes.text}`]: {
    paddingBottom: "1rem",
    color: "#666666",
  },
}));

interface Props {
  appState: Query;
  modal?: boolean;
}

export default function SodInstitutionDetails({ appState, modal }: Props) {
  const [sodInstitutionData, setSodInstitutionData] = React.useState<KeyValue>(
    {},
  );

  const detailsState = React.useMemo(() => {
    return {
      cert: appState.filters.cert,
      year: appState.filters.lastYear,
      OTSDocket: appState.filters.OTSDocket,
      rssdid: appState.filters.rssdid,
      rssdhcr: appState.filters.bhcRSSD,
    };
  }, [
    appState.filters.cert,
    appState.filters.lastYear,
    appState.filters.OTSDocket,
    appState.filters.rssdid,
    appState.filters.bhcRSSD,
  ]);

  const [previousDetailsState, setPreviousDetailsState] =
    React.useState<KeyValue>(detailsState);

  const contactLink = `https://ask.fdic.gov/fdicinformationandsupportcenter/s`;

  const consumerAssistanceLookup: KeyValue = {
    FDIC: {
      link: "https://ask.fdic.gov/fdicinformationandsupportcenter/s/?language=en_US",
      label: "Complaints & Questions with Personal Information",
      internal: true,
    },
    OCC: {
      link: "http://www.helpwithmybank.gov",
      label: "HelpWithMyBank.gov",
    },
    FED: {
      link: "http://www.federalreserveconsumerhelp.gov",
      label: "Federal Reserve Consumer Help",
    },
    OTS: {
      link: "",
      label: "",
    },
    STATE: {
      link: "",
      label: "",
    },
  };

  const consumerAssistance = sodInstitutionData
    ? consumerAssistanceLookup[sodInstitutionData.REGAGNT]
    : undefined;

  const consumerAssistanceHoverText =
    "The link is to the consumer help section of the bank's primary Federal regulator, which is the agency mainly responsible for solving a problem or concern.";

  const [initialLoad, setInitialLoad] = React.useState(true);
  useEffect(() => {
    let isMounted = true;
    let searchType = "";
    let searchValue = "";

    if (detailsState.cert) {
      searchType = "CERT";
      searchValue = detailsState.cert;
    }
    if (detailsState.OTSDocket) {
      searchType = "DOCKET";
      searchValue = detailsState.OTSDocket;
    }
    if (detailsState.rssdid) {
      searchType = "RSSDID";
      searchValue = detailsState.rssdid;
    }

    if (detailsState.rssdhcr) {
      searchType = "RSSDHCR";
      searchValue = detailsState.rssdhcr;
    }

    if (
      (initialLoad ||
        JSON.stringify(detailsState) !==
          JSON.stringify(previousDetailsState)) &&
      searchType &&
      searchValue &&
      detailsState.year
    ) {
      setInitialLoad(false);
      setPreviousDetailsState(detailsState);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=${searchType}:"${searchValue}" AND YEAR:${
            detailsState.year
          }`,
        )
          .then((response) => {
            if (isMounted) {
              setSodInstitutionData(response.data[0]);
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [detailsState, initialLoad, previousDetailsState]);

  return sodInstitutionData &&
    sodInstitutionData.CERT &&
    appState.filters.bhcRSSD === "" ? (
    <Root
      sx={{
        "& div.MuiTypography-body1": { lineHeight: "1rem" },
        "display": "inline-block",
        "& button#return-to-landing-page-button": {
          marginBottom: "2rem",
        },
        "& hr": {
          margin: "7px 0 12px 0",
        },
        "& a": {
          textTransform: "none",
        },
        "width": "100%",
      }}
      id={"bank-detail"}
    >
      {!modal && (
        <>
          <Typography variant="h2">{sodInstitutionData.NAMEFULL}</Typography>
          <Divider variant="fullWidth" />
        </>
      )}
      <Typography component={"div"} className={classes.subHeader}>
        <Typography variant={"h2"} style={{ paddingTop: 16 }}>
          Institution Details
        </Typography>
        <Typography id={"data-as-of-date"}>
          Data as of 06/30/{sodInstitutionData.YEAR}
        </Typography>
      </Typography>
      <Grid container spacing={2}>
        <Grid className={classes.gridItem} size={4}>
          <Typography component="div" id="cert">
            <CardListCondensedItem
              itemLabel="FDIC Cert #"
              itemValue={sodInstitutionData.CERT}
            />
          </Typography>
          <Typography component="div" id="date-established">
            <CardListCondensedItem
              itemLabel="Established"
              itemValue={formatDate(sodInstitutionData.SIMS_ESTABLISHED_DATE)}
            />
          </Typography>
          <Typography component="div" id="charter-class">
            <CardListCondensedItem
              itemLabel="Bank Charter Class"
              itemValue={bankClassLookup(sodInstitutionData.BKCLASS)}
            />
          </Typography>
          <Typography component="div" id="headquarter-address">
            <CardListCondensedItem
              itemLabel="Main Office Address"
              itemValue={sodInstitutionData.ADDRESS}
            />
            {isDefinedAndTrue(sodInstitutionData.ADDRESS2) ? (
              <CardListCondensedItem
                itemValueId={`address_line_2_${sodInstitutionData.CERT}`}
                itemValue={sodInstitutionData.ADDRESS2}
              />
            ) : (
              ""
            )}
            <CardListCondensedItem
              itemValue={`${sodInstitutionData.CITY}, ${sodInstitutionData.STALP} ${sodInstitutionData.ZIP}`}
            />
          </Typography>
        </Grid>
        <Grid className={classes.gridItem} size={4}>
          <Typography component="div" id="primary-regulator">
            <CardListCondensedItem
              itemLabel="Primary Federal Regulator"
              itemValue={getPrimaryRegulator(sodInstitutionData.REGAGNT)}
            />
          </Typography>
          {sodInstitutionData.NAMEHCR ? (
            <Typography component="div" id="parent-holding-company">
              <CardListCondensedItem
                itemLabel="Parent Holding Company"
                itemValue={sodInstitutionData.NAMEHCR}
              />
            </Typography>
          ) : (
            <></>
          )}
          {sodInstitutionData.RSSDHCR && sodInstitutionData.RSSDHCR !== 0 ? (
            <Typography component="div" id="holding-company-id">
              <CardListCondensedItem
                itemLabel="Holding Company ID"
                itemValue={sodInstitutionData.RSSDHCR}
              />
            </Typography>
          ) : (
            <></>
          )}
          {sodInstitutionData.DOCKET ? (
            <Typography component="div" id="OTS-docket-number">
              <CardListCondensedItem
                itemLabel="OTS Docket Number"
                itemValue={sodInstitutionData.DOCKET}
              />
            </Typography>
          ) : (
            <></>
          )}
          {sodInstitutionData.RSSDID ? (
            <Typography component="div" id="federal-reserve-id">
              <CardListCondensedItem
                itemLabel="Federal Reserve ID"
                itemValue={sodInstitutionData.RSSDID}
              />
            </Typography>
          ) : (
            <></>
          )}
        </Grid>
        <Grid className={classes.gridItem} size={4}>
          {consumerAssistance?.label !== "" ? (
            <Typography component="div" id="consumer-assistance">
              <CardListCondensedItem
                itemLabel="Consumer Assistance"
                itemValue=""
              />
              {consumerAssistance?.internal ? (
                <LinkToolTip
                  title={consumerAssistanceHoverText}
                  enterDelay={250}
                  TransitionComponent={Fade}
                  classes={{
                    tooltip: classes.condensedTooltip,
                  }}
                >
                  <Link
                    className={"consumer_assistance_link"}
                    href={consumerAssistance?.link}
                    underline="always"
                  >
                    {consumerAssistance?.label}
                  </Link>
                </LinkToolTip>
              ) : (
                <LeavingFDICDialog
                  webAddress={consumerAssistance?.link}
                  toolTipText={consumerAssistanceHoverText}
                  label={consumerAssistance?.label}
                />
              )}
            </Typography>
          ) : (
            ""
          )}

          <Typography component="div" id="fdic-contact">
            <CardListCondensedItem
              itemLabel="Contact the FDIC"
              itemValue={""}
            />
            <LinkToolTip
              classes={{
                tooltip: classes.tooltipPopper,
              }}
              title={contactLink}
              enterDelay={250}
              TransitionComponent={Fade}
            >
              <Link href={contactLink} underline="always">
                Questions about Bank Information
              </Link>
            </LinkToolTip>
          </Typography>
        </Grid>
      </Grid>
    </Root>
  ) : sodInstitutionData &&
    sodInstitutionData.RSSDHCR &&
    appState.filters.cert === "" ? (
    <Root
      sx={{
        "& div.MuiTypography-body1": { lineHeight: "1rem" },
        "display": "inline-block",
        "& button#return-to-landing-page-button": {
          marginBottom: "2rem",
        },
        "& hr": {
          margin: "7px 0 12px 0",
        },
        "& a": {
          textTransform: "none",
        },
        "width": "100%",
      }}
      id={"bhc-detail"}
    >
      {!modal && (
        <>
          <Typography variant="h2">{sodInstitutionData.NAMEHCR}</Typography>
          <Divider variant="fullWidth" />
        </>
      )}
      <Typography component={"div"} className={classes.subHeader}>
        <Typography variant={"h2"} style={{ paddingTop: 16 }}>
          Bank Holding Company Details
        </Typography>
        <Typography id={"data-as-of-date"}>
          Data as of 06/30/{sodInstitutionData.YEAR}
        </Typography>
      </Typography>
      <Grid container spacing={2}>
        <Grid className={classes.gridItem} size={4}>
          <Typography component="div" id="rssdhcr">
            <CardListCondensedItem
              itemLabel="Bank Holding Company ID"
              itemValue={sodInstitutionData.RSSDHCR}
            />
          </Typography>
          <Typography component="div" id="primary-regulator">
            <CardListCondensedItem
              itemLabel="Primary Federal Regulator"
              itemValue={getPrimaryRegulator(sodInstitutionData.REGAGNT)}
            />
          </Typography>
          {consumerAssistance?.label !== "" ? (
            <Typography component="div" id="consumer-assistance">
              <CardListCondensedItem
                itemLabel="Consumer Assistance"
                itemValue=""
              />
              {consumerAssistance?.internal ? (
                <LinkToolTip
                  title={consumerAssistanceHoverText}
                  enterDelay={250}
                  TransitionComponent={Fade}
                  classes={{
                    tooltip: classes.condensedTooltip,
                  }}
                >
                  <Link
                    className={"consumer_assistance_link"}
                    href={consumerAssistance?.link}
                    underline="always"
                  >
                    {consumerAssistance?.label}
                  </Link>
                </LinkToolTip>
              ) : (
                <LeavingFDICDialog
                  webAddress={consumerAssistance?.link}
                  toolTipText={consumerAssistanceHoverText}
                  label={consumerAssistance?.label}
                />
              )}
            </Typography>
          ) : (
            ""
          )}
        </Grid>
        <Grid className={classes.gridItem} size={8}>
          <Typography component="div">
            <CardListCondensedItem
              itemLabel="BHCs or Bank Holding Companies"
              itemValue=""
            />
            <Typography variant="body1" className={classes.text}>
              This search will return results for top regulatory bank holding
              companies and their FDIC-insured subsidiaries. The regulatory top
              holder is assigned by the Federal Reserve Board based on ownership
              and control percentages.
            </Typography>
            <Typography variant="body1" className={classes.text}>
              Thrift bank holding company information is not available.
            </Typography>
            <Typography
              variant="body1"
              className={classes.text}
              component="span"
            >
              Official bank holding company information can be found by visiting
              the website of the{" "}
              <LeavingFDICDialog
                webAddress={"https://www.federalreserveconsumerhelp.gov/"}
                toolTipText={"https://www.federalreserveconsumerhelp.gov/"}
                label={"Federal Reserve System - National Information Center."}
              />
            </Typography>
          </Typography>
        </Grid>
      </Grid>
    </Root>
  ) : (
    <Root>
      <Typography variant="h2">No Results</Typography>
      <Divider variant="fullWidth" />
    </Root>
  );
}
